import React, { Component } from 'react'
import { connect } from 'react-redux'
import { withI18n, withI18nProps } from '@lingui/react'
import compose from 'lodash/fp/compose'
import isEmpty from 'lodash/isEmpty'
import last from 'lodash/last'
import map from 'lodash/fp/map'
import join from 'lodash/fp/join'

import env from 'env'
import { doesUrlEndInTitle } from 'client/bookmate/helpers/url-check-helpers'

import prepareComponent from 'client/shared/decorators/prepare-component'

import urlFor from 'shared/tools/url-helper'

import {
  load,
  loadEmotionRating,
} from 'client/bookmate/reducers/comicbook-reducer'

import { getComicbookById } from 'client/bookmate/reducers/comicbooks-reducer'

import MetaTwinBox from 'client/shared/boxes/meta-twin-box'
import Meta from 'client/shared/blocks/meta'
import MetaTitle from 'client/shared/blocks/meta-title'
import { MetaDescription } from 'client/shared/blocks/meta-description'
import MetaDeeplink from 'client/shared/blocks/meta-deeplink'

import { ComicbookProps } from 'client/shared/types/comicbook'
import { Dispatch, State } from 'shared/types/redux'
import { State as AppState } from 'client/shared/reducers/app-reducer'
import { CurrentUserState } from 'client/shared/types/current-user'
import { Location, Route } from 'client/shared/types/react-router'

type Props = {
  app: AppState
  currentUser: CurrentUserState
  comicbook: ComicbookProps
  location: Location
  dispatch: Dispatch
  children: React.ReactNode
  routes: Route[]
} & withI18nProps

const getAuthorsString = compose(join(', '), map('name'))

class ComicbookPage extends Component<Props> {
  render() {
    const { comicbook } = this.props

    return (
      <>
        {this.renderMeta()}
        {!isEmpty(comicbook) && this.props.children}
      </>
    )
  }

  renderMeta() {
    const {
      i18n,
      app: { host },
      comicbook,
      comicbook: { uuid, title: comicTitle, annotation, authors: authorsArr },
      routes,
    } = this.props

    if (isEmpty(comicbook)) return null

    const authors = getAuthorsString(authorsArr)
    const title = i18n._('book.head_title_comicbook', {
      title: comicTitle,
      authors,
      hasAuthors: !isEmpty(authors),
    })
    const ogTitle = `${comicTitle}, ${authors}`
    const ogUrl = `https://${host}${urlFor.comicbook(uuid)}`
    const text = i18n._('book.meta_description_comicbook', {
      title: comicTitle,
    })
    const description = text + (annotation ? ` ${annotation}` : '')

    return (
      <>
        <MetaTwinBox
          language={comicbook.language}
          {...(last(routes)?.path === 'readers' ? { robotsParams: {} } : {})}
        />
        <MetaTitle title={title} og={ogTitle} />
        <MetaDescription description={description} og={annotation} />
        <MetaDeeplink target="comicbook" uuid={comicbook.uuid} />
        <Meta content="books.book" property="og:type" />
        <Meta content={ogUrl} property="og:url" />
        <Meta content="false" property="al:web:should_fallback" />
        <Meta content="summary_large_image" property="twitter:card" />
        <Meta
          content={urlFor.canvasComicbook({
            comicbookId: uuid,
            mode: 'twitter',
          })}
          property="twitter:image"
        />
        <Meta
          content={urlFor.canvasComicbook({
            comicbookId: uuid,
            mode: 'facebook',
          })}
          property="og:image"
        />
      </>
    )
  }
}

const prepareComponentWrapper = prepareComponent(
  async ({ store, params }) => {
    const { uuid, splat } = params
    const { dispatch } = store

    const cyrillicPattern = /[\u0400-\u04FF]/g

    if (!uuid) return null

    if (cyrillicPattern.test(uuid)) {
      throw { response: { status: 404 } }
    }

    const actions = [dispatch(load(uuid)), dispatch(loadEmotionRating(uuid))]

    await Promise.all(actions)

    if (splat && env.isServer()) {
      const state = store.getState()
      const comicbook = getComicbookById(state, state.comicbook.uuid)
      if (doesUrlEndInTitle(comicbook.title, splat)) {
        throw {
          response: {
            status: 301,
            location: urlFor.comicbook(uuid, state.app.storedQuery),
          },
        }
      } else {
        throw { response: { status: 404 } }
      }
    }
  },
  {
    updateParams: 'uuid',
    userListener: true,
  },
)

const wrappers = compose(
  prepareComponentWrapper,
  withI18n({ update: true }),
  connect((state: State) => ({
    app: state.app,
    currentUser: state.currentUser,
    comicbook: getComicbookById(state, state.comicbook.uuid),
  })),
)

export default wrappers(ComicbookPage)
